import { callable } from './callable'
import type {
  EduBooking,
  EduBookingSettings,
  EduBookingStatus,
  EduEntitlement,
  EduEntitlementLedgerEntry,
} from '../types/schema'

export type { EduBooking, EduEntitlement }

export interface SetEduBookingSettingsInput {
  workspaceId: string
  rootId: string
  bookAheadDays?: number
  cancelWindowHours: number
  strikesToBlock: number
  strikeWindowDays: number
  blockDays: number
  usableWhen: 'PAID' | 'ASSIGNED'
  dropIn: {
    enabled: boolean
    priceMinor: number
  }
  membershipCap: { capPer: 'WEEK' | 'MONTH'; capCount: number } | null
}

export interface SetEduBookingSettingsResult {
  success: boolean
  settings: EduBookingSettings
}

export interface GetEduBookingSettingsInput {
  workspaceId: string
  rootId: string
}

export interface GetEduBookingSettingsResult {
  settings: EduBookingSettings
}

export interface GrantEduBookingCreditInput {
  workspaceId: string
  entitlementId: string
  reason: string
}

export interface GrantEduBookingCreditResult {
  success: boolean
  entitlement: EduEntitlement
}

export interface RevokeEduBookingCreditInput {
  workspaceId: string
  entitlementId: string
  reason: string
}

export interface RevokeEduBookingCreditResult {
  success: boolean
  entitlement: EduEntitlement
}

export interface RequestEduDropInInput {
  workspaceId: string
  seriesId: string
  date: string
  learnerId: string
  requestId?: string
}

export interface RequestEduDropInResult {
  success: boolean
  dueId: string
}

export interface BookEduSessionInput {
  workspaceId: string
  seriesId: string
  date: string
  learnerId: string
  entitlementId?: string
  dropIn?: boolean
  waitlist?: boolean
  requestId?: string
}

export interface BookEduSessionResult {
  success: boolean
  bookingId: string
  waitlist: boolean
  waitlistPosition?: number | null
}

export interface CancelEduBookingInput {
  workspaceId: string
  bookingId: string
  reason?: string
  waiveFee?: boolean
  waiveReason?: string
}

export interface CancelEduBookingResult {
  success: boolean
  status: EduBookingStatus
  isLateCancel?: boolean
  isStaffWaived?: boolean
  isFreeOnce?: boolean
  waived?: boolean
  freeOnce?: boolean
}

export interface MoveEduBookingInput {
  workspaceId: string
  bookingId: string
  toSeriesId: string
  toDate: string
  reason?: string
}

export interface MoveEduBookingResult {
  success: boolean
  newBookingId: string
}

export interface ListEduSessionBookingsInput {
  workspaceId: string
  seriesId: string
  date: string
}

export interface ListEduSessionBookingsResult {
  bookings: EduBooking[]
}

export interface ListEduLearnerEntitlementsInput {
  workspaceId: string
  learnerId: string
}

export interface ListEduLearnerEntitlementsResult {
  entitlements: EduEntitlement[]
}

export interface ListFamilyBookingsInput {
  workspaceId: string
  learnerId: string
}

export interface ListFamilyBookingsResult {
  bookings: EduBooking[]
}

export interface ListFamilyEntitlementsInput {
  workspaceId: string
  learnerId: string
}

export interface ListFamilyEntitlementsResult {
  entitlements: EduEntitlement[]
}

export interface ListFamilyBookableSessionsInput {
  workspaceId: string
  learnerId: string
  rootId: string
}

export interface FamilyBookableSessionChildBooking {
  id: string
  status: EduBookingStatus
  waitlistPosition?: number | null
  lateCancelNow?: boolean
  freeCancelOnce?: boolean
}

export interface FamilyBookableSessionItem {
  seriesId: string
  date: string
  startTime: string
  durationMin: number
  nodeId: string
  title: string
  seatsLeft: number
  childBooking: FamilyBookableSessionChildBooking | null
  dropInAvailable: boolean
  dropInPriceMinor: number
  [key: string]: unknown
}

export interface ListFamilyBookableSessionsResult {
  sessions: FamilyBookableSessionItem[]
}

export interface LeaveEduWaitlistInput {
  workspaceId: string
  bookingId: string
  reason?: string
}

export interface LeaveEduWaitlistResult {
  success: boolean
  removed: boolean
}

export interface ClearEduBookingBlockInput {
  workspaceId: string
  rootId: string
  learnerId: string
  reason: string
}

export interface ClearEduBookingBlockResult {
  success: boolean
  cleared: boolean
}

export interface ExtendEduEntitlementInput {
  workspaceId: string
  entitlementId: string
  newValidTo: string
  reason: string
}

export interface ExtendEduEntitlementResult {
  success: boolean
  entitlement: EduEntitlement
}

export interface FreezeEduMembershipInput {
  workspaceId: string
  entitlementId: string
  from: string
  to: string
  reason: string
}

export interface FreezeEduMembershipResult {
  success: boolean
  freezeDays: number
  entitlement: EduEntitlement
  cancelledBookingsCount: number
  shiftedDuesCount: number
}

export interface UnfreezeEduMembershipInput {
  workspaceId: string
  entitlementId: string
  reason?: string
}

export interface UnfreezeEduMembershipResult {
  success: boolean
  entitlement: EduEntitlement
}

export interface ListEduBookingSessionsInput {
  workspaceId: string
  rootId: string
  fromDate: string
  toDate: string
}

export interface EduBookingSessionItem {
  seriesId: string
  date: string
  startTime: string
  nodeId: string
  capacity: number
  bookedCount: number
  waitlistCount: number
}

export interface ListEduBookingSessionsResult {
  sessions: EduBookingSessionItem[]
}

export interface ListEduLearnerBookingDetailInput {
  workspaceId: string
  learnerId: string
  rootId?: string
  limit?: number
}

export interface EduLearnerBookingDetailResult {
  entitlements: Array<
    EduEntitlement & {
      ledger?: EduEntitlementLedgerEntry[]
      history?: Record<string, unknown>[]
    }
  >
  bookings: EduBooking[]
  upcomingBookings: EduBooking[]
  recentBookings: EduBooking[]
  strikeCount: number
  activeBlock: { blockedUntil: string; rootId: string; programmeName?: string } | null
  activeBlocks?: Array<{ blockedUntil: string; rootId: string; programmeName?: string }>
  strikes: Record<string, unknown>[]
  programmes?: Record<
    string,
    {
      rootId: string
      programmeName?: string
      strikeCount: number
      activeBlock: { blockedUntil: string; rootId: string; programmeName?: string } | null
      strikes: Record<string, unknown>[]
    }
  >
}

export const setEduBookingSettings = async (
  input: SetEduBookingSettingsInput,
): Promise<SetEduBookingSettingsResult> =>
  callable<SetEduBookingSettingsInput, SetEduBookingSettingsResult>('setEduBookingSettings', input)

export const getEduBookingSettings = async (
  input: GetEduBookingSettingsInput,
): Promise<GetEduBookingSettingsResult> =>
  callable<GetEduBookingSettingsInput, GetEduBookingSettingsResult>('getEduBookingSettings', input)

export const grantEduBookingCredit = async (
  input: GrantEduBookingCreditInput,
): Promise<GrantEduBookingCreditResult> =>
  callable<GrantEduBookingCreditInput, GrantEduBookingCreditResult>('grantEduBookingCredit', input)

export const revokeEduBookingCredit = async (
  input: RevokeEduBookingCreditInput,
): Promise<RevokeEduBookingCreditResult> =>
  callable<RevokeEduBookingCreditInput, RevokeEduBookingCreditResult>('revokeEduBookingCredit', input)

export const requestEduDropIn = async (
  input: RequestEduDropInInput,
): Promise<RequestEduDropInResult> =>
  callable<RequestEduDropInInput, RequestEduDropInResult>('requestEduDropIn', input)

export const bookEduSession = async (
  input: BookEduSessionInput,
): Promise<BookEduSessionResult> =>
  callable<BookEduSessionInput, BookEduSessionResult>('bookEduSession', input)

export const cancelEduBooking = async (
  input: CancelEduBookingInput,
): Promise<CancelEduBookingResult> =>
  callable<CancelEduBookingInput, CancelEduBookingResult>('cancelEduBooking', input)

export const moveEduBooking = async (
  input: MoveEduBookingInput,
): Promise<MoveEduBookingResult> =>
  callable<MoveEduBookingInput, MoveEduBookingResult>('moveEduBooking', input)

export const listEduSessionBookings = async (
  input: ListEduSessionBookingsInput,
): Promise<ListEduSessionBookingsResult> =>
  callable<ListEduSessionBookingsInput, ListEduSessionBookingsResult>('listEduSessionBookings', input)

export const listEduLearnerEntitlements = async (
  input: ListEduLearnerEntitlementsInput,
): Promise<ListEduLearnerEntitlementsResult> =>
  callable<ListEduLearnerEntitlementsInput, ListEduLearnerEntitlementsResult>('listEduLearnerEntitlements', input)

export const listFamilyBookings = async (
  input: ListFamilyBookingsInput,
): Promise<ListFamilyBookingsResult> =>
  callable<ListFamilyBookingsInput, ListFamilyBookingsResult>('listFamilyBookings', input)

export const listFamilyEntitlements = async (
  input: ListFamilyEntitlementsInput,
): Promise<ListFamilyEntitlementsResult> =>
  callable<ListFamilyEntitlementsInput, ListFamilyEntitlementsResult>('listFamilyEntitlements', input)

export const leaveEduWaitlist = async (
  input: LeaveEduWaitlistInput,
): Promise<LeaveEduWaitlistResult> =>
  callable<LeaveEduWaitlistInput, LeaveEduWaitlistResult>('leaveEduWaitlist', input)

export const clearEduBookingBlock = async (
  input: ClearEduBookingBlockInput,
): Promise<ClearEduBookingBlockResult> =>
  callable<ClearEduBookingBlockInput, ClearEduBookingBlockResult>('clearEduBookingBlock', input)

export const extendEduEntitlement = async (
  input: ExtendEduEntitlementInput,
): Promise<ExtendEduEntitlementResult> =>
  callable<ExtendEduEntitlementInput, ExtendEduEntitlementResult>('extendEduEntitlement', input)

export const freezeEduMembership = async (
  input: FreezeEduMembershipInput,
): Promise<FreezeEduMembershipResult> =>
  callable<FreezeEduMembershipInput, FreezeEduMembershipResult>('freezeEduMembership', input)

export const unfreezeEduMembership = async (
  input: UnfreezeEduMembershipInput,
): Promise<UnfreezeEduMembershipResult> =>
  callable<UnfreezeEduMembershipInput, UnfreezeEduMembershipResult>('unfreezeEduMembership', input)

export const listEduBookingSessions = async (
  input: ListEduBookingSessionsInput,
): Promise<ListEduBookingSessionsResult> =>
  callable<ListEduBookingSessionsInput, ListEduBookingSessionsResult>('listEduBookingSessions', input)

export const listEduLearnerBookingDetail = async (
  input: ListEduLearnerBookingDetailInput,
): Promise<EduLearnerBookingDetailResult> =>
  callable<ListEduLearnerBookingDetailInput, EduLearnerBookingDetailResult>('listEduLearnerBookingDetail', input)

export const listFamilyBookableSessions = async (
  input: ListFamilyBookableSessionsInput,
): Promise<ListFamilyBookableSessionsResult> =>
  callable<ListFamilyBookableSessionsInput, ListFamilyBookableSessionsResult>('listFamilyBookableSessions', input)

